// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The building model's spaces on the locked plan (frontend-ayw.8): which
 * spaces a storey focus shows, where a space's name goes, what it says and
 * whether it fits. Spaces come from `GET /spaces` (ruxd#11) as world x/y
 * polygons — an open outer ring plus holes — with the storey they stand on
 * and their floor height; the Ground projects them and draws them as an SVG
 * layer over the plan (`components/bygning/PlanLayer.tsx`).
 *
 * Pure: no React, no DOM.
 */

import type { PlanXY, SolidSpace } from '../api/types';

export type Ring = readonly (readonly [number, number])[];

export interface SpacePolygon {
  outer: Ring;
  holes: readonly Ring[];
}

/** What the plan shows of the spaces under a storey focus. */
export interface PlanSpaces {
  /** Filled and labelled. */
  shown: SolidSpace[];
  /** Outline only, no label (the other storeys, without a focus). */
  ghost: SolidSpace[];
}

/**
 * The spaces to draw for storey `storeyId` (null = no storey focus): the
 * storey's own, filled and named, the rest hidden. Without a focus a
 * building whose spaces all stand on one storey shows them all; one with
 * several storeys draws every space as a faint outline only — their names
 * would stack on top of each other.
 */
export function planSpaces(spaces: readonly SolidSpace[] | null | undefined, storeyId: number | null): PlanSpaces {
  const usable = (spaces ?? []).filter((s) => s.polygon.outer.length >= 3);
  // A space on no stored storey (`storey_id` null) is shown only with no focus.
  if (storeyId !== null) return { shown: usable.filter((s) => s.storey_id === storeyId), ghost: [] };
  const storeys = new Set(usable.map((s) => s.storey_id));
  if (storeys.size <= 1) return { shown: usable, ghost: [] };
  return { shown: [], ghost: usable };
}

/** Signed shoelace area of a ring (positive counter-clockwise). */
export function ringArea(ring: Ring): number {
  let sum = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    sum += (ring[j][0] - ring[i][0]) * (ring[j][1] + ring[i][1]);
  }
  return sum / 2;
}

/** Area of a polygon with holes, m² (the holes taken out, whatever their winding). */
export function polygonArea(polygon: SpacePolygon): number {
  return Math.max(0, Math.abs(ringArea(polygon.outer)) - polygon.holes.reduce((sum, h) => sum + Math.abs(ringArea(h)), 0));
}

/** The space's area: the stored one, else measured off its polygon. */
export function spaceArea(space: SolidSpace): number {
  return space.area !== null && Number.isFinite(space.area) ? space.area : polygonArea(space.polygon);
}

/** Even-odd test of (x, y) against one ring. */
export function pointInRing(x: number, y: number, ring: Ring): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Inside the outer ring and in none of the holes. */
export function pointInPolygon(x: number, y: number, polygon: SpacePolygon): boolean {
  return pointInRing(x, y, polygon.outer) && !polygon.holes.some((h) => pointInRing(x, y, h));
}

/** The space under world (x, y): the smallest one that contains it (a room inside an open plan wins), or null. */
export function spaceAt<T extends SolidSpace>(spaces: readonly T[], x: number, y: number): T | null {
  let best: T | null = null;
  let bestArea = Infinity;
  for (const s of spaces) {
    if (!pointInPolygon(x, y, s.polygon)) continue;
    const area = polygonArea(s.polygon);
    if (area < bestArea) {
      best = s;
      bestArea = area;
    }
  }
  return best;
}

/** Squared distance from (px, py) to segment a–b. */
function segDistSq(px: number, py: number, a: readonly [number, number], b: readonly [number, number]): number {
  let x = a[0];
  let y = a[1];
  let dx = b[0] - x;
  let dy = b[1] - y;
  if (dx !== 0 || dy !== 0) {
    const t = ((px - x) * dx + (py - y) * dy) / (dx * dx + dy * dy);
    if (t > 1) {
      x = b[0];
      y = b[1];
    } else if (t > 0) {
      x += dx * t;
      y += dy * t;
    }
  }
  dx = px - x;
  dy = py - y;
  return dx * dx + dy * dy;
}

/** Signed distance from (x, y) to the polygon's boundary: positive inside, negative outside. */
export function signedDistance(x: number, y: number, polygon: SpacePolygon): number {
  let min = Infinity;
  for (const ring of [polygon.outer, ...polygon.holes]) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
      min = Math.min(min, segDistSq(x, y, ring[i], ring[j]));
    }
  }
  const d = Math.sqrt(min);
  return pointInPolygon(x, y, polygon) ? d : -d;
}

/** Where a space's name goes, and how much room it has there. */
export interface LabelAnchor {
  x: number;
  y: number;
  /** Distance to the nearest edge (hole edges included), metres. */
  radius: number;
}

/**
 * The pole of inaccessibility (Mapbox's polylabel): the point inside the
 * polygon furthest from every edge, holes included, to `precision` metres.
 * The centroid would land in the hole of a ring-shaped corridor or outside
 * an L-shaped room; this never does. A degenerate polygon answers its bbox
 * centre with radius 0.
 */
export function labelAnchor(polygon: SpacePolygon, precision = 0.05): LabelAnchor {
  const outer = polygon.outer;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of outer) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  const width = maxX - minX;
  const height = maxY - minY;
  const size = Math.min(width, height);
  if (!(size > 0) || !Number.isFinite(size)) {
    return { x: (minX + maxX) / 2 || 0, y: (minY + maxY) / 2 || 0, radius: 0 };
  }

  interface Cell {
    x: number;
    y: number;
    h: number;
    d: number;
    max: number;
  }
  const cell = (x: number, y: number, h: number): Cell => {
    const d = signedDistance(x, y, polygon);
    return { x, y, h, d, max: d + h * Math.SQRT2 };
  };

  // Seed: the area centroid of the outer ring (often a good start), and the bbox centre.
  let best = cell(minX + width / 2, minY + height / 2, 0);
  const a = ringArea(outer);
  if (a !== 0) {
    let cx = 0;
    let cy = 0;
    for (let i = 0, j = outer.length - 1; i < outer.length; j = i, i += 1) {
      const f = outer[i][0] * outer[j][1] - outer[j][0] * outer[i][1];
      cx += (outer[i][0] + outer[j][0]) * f;
      cy += (outer[i][1] + outer[j][1]) * f;
    }
    const centroid = cell(cx / (6 * a), cy / (6 * a), 0);
    if (centroid.d > best.d) best = centroid;
  }

  const queue: Cell[] = [];
  const h = size / 2;
  for (let x = minX; x < maxX; x += size) {
    for (let y = minY; y < maxY; y += size) queue.push(cell(x + h, y + h, h));
  }
  // A small polygon's queue stays short: a sorted array is plenty.
  let guard = 0;
  while (queue.length > 0 && guard < 10_000) {
    guard += 1;
    queue.sort((p, q) => q.max - p.max);
    const c = queue.shift() as Cell;
    if (c.d > best.d) best = c;
    if (c.max - best.d <= precision) continue;
    const k = c.h / 2;
    queue.push(cell(c.x - k, c.y - k, k), cell(c.x + k, c.y - k, k), cell(c.x - k, c.y + k, k), cell(c.x + k, c.y + k, k));
  }
  return { x: best.x, y: best.y, radius: Math.max(0, best.d) };
}

/** An area in Danish: "16,3 m²" (one decimal below 100 m², whole square metres above). */
export function formatAreaDa(m2: number): string {
  const digits = m2 < 100 ? 1 : 0;
  return `${m2.toLocaleString('da-DK', { minimumFractionDigits: digits, maximumFractionDigits: digits })} m²`;
}

/** A space's label text: its name (or "Rum uden navn") and its area, when known. */
export interface SpaceLabelText {
  name: string;
  area: string | null;
  /** One line: "Køkken · 16,3 m²". */
  line: string;
}

export const UNNAMED_SPACE = 'Rum uden navn';

export function spaceLabelText(space: SolidSpace): SpaceLabelText {
  const name = space.name.trim() || UNNAMED_SPACE;
  const m2 = spaceArea(space);
  const area = m2 > 0 && Number.isFinite(m2) ? formatAreaDa(m2) : null;
  return { name, area, line: area ? `${name} · ${area}` : name };
}

/** Rough width of `text` set at `fontPx` in the UI sans (Archivo averages ~0.56 em per glyph). */
export function textWidth(text: string, fontPx: number): number {
  return text.length * fontPx * 0.56;
}

export type LabelFit = 'one' | 'two' | 'name' | 'none';

/**
 * How much of a space's label fits in the room on screen around its anchor
 * (`availW` × `availH` CSS px): name and area on one line, on two, the name
 * alone, or nothing — a label hanging over the walls into the next room
 * reads as that room's.
 */
export function fitLabel(text: SpaceLabelText, fontPx: number, availW: number, availH: number): LabelFit {
  const lineH = fontPx * 1.3;
  if (availH >= lineH) {
    if (textWidth(text.line, fontPx) <= availW) return text.area ? 'one' : 'name';
    const nameW = textWidth(text.name, fontPx);
    if (text.area && availH >= lineH * 2 && Math.max(nameW, textWidth(text.area, fontPx)) <= availW) return 'two';
    if (nameW <= availW) return 'name';
  }
  return 'none';
}

/**
 * An SVG path for a polygon already projected to screen (`outer` and each
 * hole as xy pairs, stride 2) — `M … Z` per ring, drawn with
 * `fill-rule: evenodd` so the holes stay open. Null when any vertex is off
 * screen (`NaN`): a half-projected polygon would draw a wrong shape.
 */
export function ringsPath(rings: readonly Float32Array[] | readonly number[][]): string | null {
  const parts: string[] = [];
  for (const ring of rings) {
    if (ring.length < 6) continue;
    let d = '';
    for (let i = 0; i < ring.length; i += 2) {
      const x = ring[i];
      const y = ring[i + 1];
      if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
      d += `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
    }
    parts.push(`${d}Z`);
  }
  return parts.length > 0 ? parts.join('') : null;
}

/** A polygon's rings at height `z` as world xyz, stride 3, outer first — for one projection call. */
export function ringsXyz(polygon: { outer: readonly PlanXY[] | Ring; holes: readonly (readonly PlanXY[] | Ring)[] }, z: number): {
  xyz: Float64Array;
  /** Vertex count per ring, in order. */
  counts: number[];
} {
  const rings = [polygon.outer, ...polygon.holes];
  const counts = rings.map((r) => r.length);
  const xyz = new Float64Array(counts.reduce((a, b) => a + b, 0) * 3);
  let k = 0;
  for (const ring of rings) {
    for (const [x, y] of ring) {
      xyz[k] = x;
      xyz[k + 1] = y;
      xyz[k + 2] = z;
      k += 3;
    }
  }
  return { xyz, counts };
}

/** Split a projected xy array (stride 2) back into rings by `counts`. */
export function splitRings(xy: Float32Array, counts: readonly number[]): Float32Array[] {
  const out: Float32Array[] = [];
  let at = 0;
  for (const n of counts) {
    out.push(xy.subarray(at * 2, (at + n) * 2));
    at += n;
  }
  return out;
}

/** A label's box on screen: its centre and size, CSS px. */
export interface LabelBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** A projected ring (xy, stride 2) as points. */
function screenRing(xy: ArrayLike<number>): [number, number][] {
  const out: [number, number][] = [];
  for (let i = 0; i + 1 < xy.length; i += 2) out.push([xy[i], xy[i + 1]]);
  return out;
}

/**
 * Where a space's name goes on screen: the first of a few spots — its
 * anchor, then just below / above / beside each marker that is in the way,
 * then above / below the anchor — whose whole box stays inside the room
 * (outside its holes) and clear of every marker (by `markerR`). Null when
 * none is: the caller keeps the anchor rather than push the name into the
 * next room or onto another marker.
 */
export function chooseLabelSpot(
  anchor: readonly [number, number],
  size: { w: number; h: number },
  rings: { outer: ArrayLike<number>; holes: readonly ArrayLike<number>[] },
  markers: readonly (readonly [number, number])[],
  markerR: number,
): LabelBox | null {
  const outer = screenRing(rings.outer);
  const holes = rings.holes.map(screenRing);
  const { w, h } = size;
  const gap = 4;
  const fits = (x: number, y: number): boolean => {
    const corners: [number, number][] = [
      [x - w / 2, y - h / 2],
      [x + w / 2, y - h / 2],
      [x + w / 2, y + h / 2],
      [x - w / 2, y + h / 2],
    ];
    for (const [cx, cy] of corners) {
      if (!pointInRing(cx, cy, outer) || holes.some((hole) => pointInRing(cx, cy, hole))) return false;
    }
    return markers.every(
      ([mx, my]) => mx < x - w / 2 - markerR || mx > x + w / 2 + markerR || my < y - h / 2 - markerR || my > y + h / 2 + markerR,
    );
  };
  const candidates: [number, number][] = [[anchor[0], anchor[1]]];
  for (const [mx, my] of markers) {
    if (Math.abs(mx - anchor[0]) > w / 2 + markerR * 2 || Math.abs(my - anchor[1]) > h / 2 + markerR * 2) continue;
    candidates.push(
      [mx, my + markerR + gap + h / 2],
      [mx, my - markerR - gap - h / 2],
      [mx + markerR + gap + w / 2, my],
      [mx - markerR - gap - w / 2, my],
    );
  }
  for (let k = 1; k <= 3; k += 1) {
    candidates.push([anchor[0], anchor[1] + k * (h + gap)], [anchor[0], anchor[1] - k * (h + gap)]);
  }
  for (const [x, y] of candidates) {
    if (Number.isFinite(x) && Number.isFinite(y) && fits(x, y)) return { x, y, w, h };
  }
  return null;
}
